import {
  Address,
  OrderPayment,
  PaymentRequest,
  ServicePaymentMethodTypes,
  UserEmailConsentResponse,
} from '~app/modules/checkout/models';
import { CriteoTransactionParams } from '~app/modules/checkout/models/criteo.model';
import { SUBSIDIARY_SCOPE_CHECKOUT } from '~core/modules/identity/constants/identity.constant';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { CountryCode } from '~shared/constants';
import {
  CHECKOUT_LOCALSTORAGE_KEY_PAYMENT,
  CHECKOUT_LOCALSTORAGE_PURCHASE_ORDER_NUMBER,
  CHECKOUT_LOCALSTORAGE_REACT_PHONENUMBER,
  CHECKOUT_LOCALSTORAGE_TMP_ADRS,
  CHECKOUT_LOCALSTORAGE_TMP_CUSTOMER_PURCHASE_HISTORY,
  CHECKOUT_LOCALSTORAGE_TMP_EMAIL_CONSENT,
  CHECKOUT_LOCALSTORAGE_TMP_ORDER_NUMBER,
  GLOBAL_SESSION_ID,
  CHECKOUT_SESSION_LOG_ORDER_NUMBER,
  CHECKOUT_SESSION_STORAGE_COMBINED_CUSTOMER_CONSENT_COMBINE,
  CHECKOUT_SESSION_STORAGE_COMBINED_CUSTOMER_CONSENT_EMAIL,
  CHECKOUT_SESSION_STORAGE_COMBINED_CUSTOMER_CONSENT_PHONE,
  CHECKOUT_SESSION_STORAGE_COMBINED_CUSTOMER_OPT_OUT_CHECKBOX,
  CHECKOUT_SESSION_STORAGE_TMP_CRITEO_TRANSACTION_DATA,
  CHECKOUT_SESSION_STORAGE_TMP_DISCOUNT_CODE,
  CHECKOUT_SESSION_STORAGE_TMP_TAX_NUMBER,
  CHECKOUT_SESSION_STORAGE_TMP_VAT_ID,
  CHECKOUT_SESSION_STORAGE_TMP_ZENLOOP_DATA,
  PaymentErrorMessages,
  PaymentFailedAssistedSalesErrorMap,
  PaymentMethodOptions,
  SUBSIDIARY_NEEDS_APPROVAL,
} from '../constants';
import { LanguageCode } from '../models/language-enum';
import { PaypalBuyerLocale, PaypalLocale } from '../models/paypal-locale';
import { uuidv4 } from '~shared/utils/uuid.util';

export namespace CheckoutCommonUtil {
  const SPToPaymentMethodMap: Record<ServicePaymentMethodTypes, PaymentMethodOptions> = {
    [ServicePaymentMethodTypes.RatePayDirectDebit]: PaymentMethodOptions.RatePayDirectDebit,
    [ServicePaymentMethodTypes.RatePayInvoice]: PaymentMethodOptions.RatePayInvoice,
    [ServicePaymentMethodTypes.CreditCard]: PaymentMethodOptions.CreditCard,
    [ServicePaymentMethodTypes.Paypal]: PaymentMethodOptions.Paypal,
    [ServicePaymentMethodTypes.DirectEbanking]: PaymentMethodOptions.DirectEbanking,
    [ServicePaymentMethodTypes.IDeal]: PaymentMethodOptions.IDeal,
    [ServicePaymentMethodTypes.PayPal_ins_FR]: PaymentMethodOptions.PayPal_ins_FR,
    [ServicePaymentMethodTypes.PayPal_ins_ES]: PaymentMethodOptions.PayPal_ins_ES,
    [ServicePaymentMethodTypes.PayPal_ins_IT]: PaymentMethodOptions.PayPal_ins_IT,
    [ServicePaymentMethodTypes.SepaDebitDirect]: PaymentMethodOptions.SepaDebitDirect,
    [ServicePaymentMethodTypes.Billie]: PaymentMethodOptions.Billie,
    [ServicePaymentMethodTypes.MetroInvoice]: PaymentMethodOptions.MetroInvoice,
    [ServicePaymentMethodTypes.PayByBank]: PaymentMethodOptions.PayByBank,
  };
  export const ReviewPaymentLabel = {
    [ServicePaymentMethodTypes.RatePayInvoice]: {
      target: 'payment-method-invoice',
      label: 'CHECKOUT.PAYMENT.RATE_PAY_INVOICE',
    },
    [ServicePaymentMethodTypes.RatePayDirectDebit]: {
      target: 'payment-method-direct-debit',
      label: 'CHECKOUT.PAYMENT.DIRECT_DEBIT',
    },
    [ServicePaymentMethodTypes.SepaDebitDirect]: {
      target: 'payment-method-direct-debit',
      label: `CHECKOUT.PAYMENT.SEPA_DIRECT_DEBIT`,
    },
    [ServicePaymentMethodTypes.DirectEbanking]: {
      target: 'payment-method-ebanking',
      label: 'CHECKOUT.PAYMENT.SOFORT',
    },
    [ServicePaymentMethodTypes.IDeal]: {
      target: 'payment-method-ideal',
      label: 'CHECKOUT.PAYMENT.IDEAL',
    },
    [ServicePaymentMethodTypes.PayPal_ins_FR]: {
      target: 'payment-method-paypal',
      label: 'CHECKOUT.REVIEW.PAYMENT.PAYPAL_INS.FRANCE',
    },
    [ServicePaymentMethodTypes.PayPal_ins_ES]: {
      target: 'payment-method-paypal',
      label: 'CHECKOUT.REVIEW.PAYMENT.PAYPAL_INS.SPAIN',
    },
    [ServicePaymentMethodTypes.PayPal_ins_IT]: {
      target: 'payment-method-paypal',
      label: 'CHECKOUT.REVIEW.PAYMENT.PAYPAL_INS.ITALY',
    },
    [ServicePaymentMethodTypes.Paypal]: {
      target: 'payment-method-paypal',
      label: 'CHECKOUT.PAYMENT.PAYPAL',
    },
    [ServicePaymentMethodTypes.MetroInvoice]: {
      target: 'payment-method-metro-invoice',
      label: 'CHECKOUT.PAYMENT.RATE_PAY_INVOICE',
    },
    [ServicePaymentMethodTypes.Billie]: {
      target: 'payment-method-billie',
      label: 'CHECKOUT.PAYMENT.BILLIE',
    },
    [ServicePaymentMethodTypes.PayByBank]: {
      target: 'payment-method-paybybank',
      label: 'CHECKOUT.PAYMENT.PAYBYBANK',
    },
  } as const;
  export function isSelectedAddressesIdentical(
    currentBillingAddress: Address,
    currentShippingAddress: Address
  ): boolean {
    if (!currentBillingAddress || (currentBillingAddress && currentBillingAddress.id === undefined)) {
      return false;
    }

    if (!currentShippingAddress || (currentShippingAddress && currentShippingAddress.id === undefined)) {
      return false;
    }

    return currentBillingAddress.id === currentShippingAddress.id;
  }

  export function mapSPToPaymentMethod(type: ServicePaymentMethodTypes): PaymentMethodOptions {
    return SPToPaymentMethodMap[type] ?? PaymentMethodOptions.NONE;
  }

  export function isUsedPaymentMethodBlocked(
    usedPaymentMethod: ServicePaymentMethodTypes,
    paymentRequest: OrderPayment
  ): boolean {
    return !paymentRequest.availablePaymentMethods?.[mapSPToPaymentMethod(usedPaymentMethod)]?.enabled;
  }

  export function savePaymentRequestToLocalStorage(paymentRequest, orderNumber): void {
    localStorage.setItem(
      CHECKOUT_LOCALSTORAGE_KEY_PAYMENT,
      JSON.stringify({
        paymentRequest,
        orderNumber,
      })
    );
  }

  export function getPaymentRequestFromLocalStorage(): { paymentRequest: PaymentRequest; orderNumber: string } | null {
    const paymentStorage = localStorage.getItem(CHECKOUT_LOCALSTORAGE_KEY_PAYMENT);

    try {
      return JSON.parse(paymentStorage);
    } catch (error) {
      console.error(error);
      this.clearPaymentRequestFromLocalStorage();
      return null;
    }
  }

  export function clearPaymentRequestFromLocalStorage() {
    localStorage.removeItem(CHECKOUT_LOCALSTORAGE_KEY_PAYMENT);
  }

  export function saveTmpAddressesToLocalStorage(addresses: {
    shippingAddress: Address;
    billingAddress: Address;
  }): void {
    localStorage.setItem(CHECKOUT_LOCALSTORAGE_TMP_ADRS, btoa(JSON.stringify(addresses)));
  }

  export function getTmpAddressesFromLocalStorage(
    isEncodingAndDecodingEnabled = false
  ): { shippingAddress: Address; billingAddress: Address } | null {
    const addresses = localStorage.getItem(CHECKOUT_LOCALSTORAGE_TMP_ADRS);

    try {
      if (addresses) {
        return isEncodingAndDecodingEnabled
          ? JSON.parse(decodeURIComponent(atob(addresses)))
          : JSON.parse(atob(addresses));
      } else {
        return null;
      }
    } catch (error) {
      console.error(error);
      this.clearTmpAddressesFromLocalStorage();
      return null;
    }
  }

  export function clearTmpAddressesFromLocalStorage() {
    localStorage.removeItem(CHECKOUT_LOCALSTORAGE_TMP_ADRS);
  }

  export function saveTmpEmailConsentToLocalStorage(consent: UserEmailConsentResponse): void {
    localStorage.setItem(CHECKOUT_LOCALSTORAGE_TMP_EMAIL_CONSENT, JSON.stringify(consent));
  }

  export function getTmpEmailConsentFromLocalStorage(): { active: boolean } {
    const consent = localStorage.getItem(CHECKOUT_LOCALSTORAGE_TMP_EMAIL_CONSENT);

    try {
      return JSON.parse(consent);
    } catch (error) {
      console.error(error);
      clearTmpEmailConsentFromLocalStorage();
      return { active: false };
    }
  }

  export function clearTmpEmailConsentFromLocalStorage() {
    localStorage.removeItem(CHECKOUT_LOCALSTORAGE_TMP_EMAIL_CONSENT);
  }

  export function saveTmpUserPurchaseHistoryToLocalStorage(userPurchaseHistory: boolean): void {
    localStorage.setItem(CHECKOUT_LOCALSTORAGE_TMP_CUSTOMER_PURCHASE_HISTORY, JSON.stringify(userPurchaseHistory));
  }

  export function getTmpUserPurchaseHistoryFromLocalStorage(): boolean {
    const hasPurchaseHistory = localStorage.getItem(CHECKOUT_LOCALSTORAGE_TMP_CUSTOMER_PURCHASE_HISTORY);

    try {
      return JSON.parse(hasPurchaseHistory);
    } catch (error) {
      console.error(error);
      clearTmpUserPurchaseHistoryFromLocalStorage();
      return null;
    }
  }

  export function clearTmpUserPurchaseHistoryFromLocalStorage() {
    localStorage.removeItem(CHECKOUT_LOCALSTORAGE_TMP_CUSTOMER_PURCHASE_HISTORY);
  }

  export function saveTmpOrderNumberToLocalStorage(orderNumber: string): void {
    localStorage.setItem(CHECKOUT_LOCALSTORAGE_TMP_ORDER_NUMBER, JSON.stringify(orderNumber));
  }

  export function getTmpOrderNumberFromLocalStorage(): string | null {
    try {
      const orderNumber = localStorage?.getItem(CHECKOUT_LOCALSTORAGE_TMP_ORDER_NUMBER);

      return JSON.parse(orderNumber);
    } catch (error) {
      console.error(error);
      clearTmpOrderNumberFromLocalStorage();
      return null;
    }
  }

  export function clearTmpOrderNumberFromLocalStorage() {
    localStorage?.removeItem(CHECKOUT_LOCALSTORAGE_TMP_ORDER_NUMBER);
  }

  export function createOrderNumberOnLocalForLogging(): void {
    const orderNumber = getTmpOrderNumberFromLocalStorage();

    if (orderNumber) {
      sessionStorage?.setItem(CHECKOUT_SESSION_LOG_ORDER_NUMBER, JSON.stringify(orderNumber));
    }
  }

  export function getOrderNumberFromLocalForLogging(): string | null {
    try {
      const orderNumberStr = sessionStorage?.getItem(CHECKOUT_SESSION_LOG_ORDER_NUMBER);

      if (!orderNumberStr) {
        return null;
      }

      return JSON.parse(orderNumberStr);
    } catch (error) {
      return null;
    }
  }

  export function createSessionIdOnSessionStorage(): void {
    const savedSessionId = getSessionIdFromSessionStorage();

    if (!savedSessionId) {
      const newSessionId = uuidv4();
      sessionStorage?.setItem(GLOBAL_SESSION_ID, newSessionId);
    }
  }

  export function getSessionIdFromSessionStorage(): string | null {
    try {
      return sessionStorage?.getItem(GLOBAL_SESSION_ID) || null;
    } catch (e) {
      return null;
    }
  }

  export function clearOrderNumberFromLocalForLogging() {
    sessionStorage?.removeItem(CHECKOUT_SESSION_LOG_ORDER_NUMBER);
  }

  export function saveTmpCriteoTransactionDataOnSessionStorage(transactionData: CriteoTransactionParams): void {
    sessionStorage.setItem(CHECKOUT_SESSION_STORAGE_TMP_CRITEO_TRANSACTION_DATA, JSON.stringify(transactionData));
  }

  export function getTmpCriteoTransactionDataOnSessionStorage(): CriteoTransactionParams | null {
    const transactionData = sessionStorage.getItem(CHECKOUT_SESSION_STORAGE_TMP_CRITEO_TRANSACTION_DATA);

    try {
      return JSON.parse(transactionData);
    } catch (error) {
      console.error(error);
      clearTmpOrderNumberFromLocalStorage();
      return null;
    }
  }

  export function clearTmpCriteoTransactionDataOnSessionStorage() {
    sessionStorage.removeItem(CHECKOUT_SESSION_STORAGE_TMP_CRITEO_TRANSACTION_DATA);
  }

  export function saveTmpDiscountCodeAppliedToSessionStorage(discount: string): void {
    sessionStorage.setItem(CHECKOUT_SESSION_STORAGE_TMP_DISCOUNT_CODE, JSON.stringify(discount));
  }

  export function getTmpDiscountCodeAppliedFromSessionStorage(): string | null {
    const discount = sessionStorage.getItem(CHECKOUT_SESSION_STORAGE_TMP_DISCOUNT_CODE);

    try {
      return JSON.parse(discount);
    } catch (error) {
      console.error(error);
      clearTmpDiscountCodeAppliedFromSessionStorage();
      return null;
    }
  }

  export function clearTmpDiscountCodeAppliedFromSessionStorage() {
    sessionStorage.removeItem(CHECKOUT_SESSION_STORAGE_TMP_DISCOUNT_CODE);
  }

  export function getPurchaseOrderNumberFromLocalStorage(): string | null {
    try {
      const purchaseOrderNumber = localStorage.getItem(CHECKOUT_LOCALSTORAGE_PURCHASE_ORDER_NUMBER);
      return purchaseOrderNumber ? String(purchaseOrderNumber) : null;
    } catch (error) {
      console.error(error);
      clearPurchaseOrderNumberFromLocalStorage();
      return null;
    }
  }

  export function clearPurchaseOrderNumberFromLocalStorage() {
    localStorage.removeItem(CHECKOUT_LOCALSTORAGE_PURCHASE_ORDER_NUMBER);
  }

  export function saveTmpTaxNumberToSessionStorage(taxNumber: string): void {
    sessionStorage.setItem(CHECKOUT_SESSION_STORAGE_TMP_TAX_NUMBER, JSON.stringify(taxNumber));
  }

  export function getTmpTaxNumberFromSessionStorage(): string | null {
    try {
      return JSON.parse(sessionStorage.getItem(CHECKOUT_SESSION_STORAGE_TMP_TAX_NUMBER));
    } catch (error) {
      console.error(error);
      clearTmpTaxNumberFromSessionStorage();
      return null;
    }
  }

  export function clearTmpZenloopDataFromSessionStorage() {
    sessionStorage.removeItem(CHECKOUT_SESSION_STORAGE_TMP_ZENLOOP_DATA);
  }

  export function saveZenloopDataToSessionStorage(paymentMethod: string, sellers: string[]): void {
    sessionStorage.setItem(CHECKOUT_SESSION_STORAGE_TMP_ZENLOOP_DATA, JSON.stringify({ paymentMethod, sellers }));
  }

  export function getZenloopDataFromSessionStorage(): { paymentMethod: string; sellers: string[] } {
    try {
      return JSON.parse(sessionStorage.getItem(CHECKOUT_SESSION_STORAGE_TMP_ZENLOOP_DATA));
    } catch (error) {
      console.error(error);
      clearTmpTaxNumberFromSessionStorage();
      return null;
    }
  }

  export function clearTmpTaxNumberFromSessionStorage() {
    sessionStorage.removeItem(CHECKOUT_SESSION_STORAGE_TMP_TAX_NUMBER);
  }

  export function saveTmpVatIdToSessionStorage(taxNumber: string): void {
    sessionStorage.setItem(CHECKOUT_SESSION_STORAGE_TMP_VAT_ID, JSON.stringify(taxNumber));
  }

  export function getTmpVatIdFromSessionStorage(): string | null {
    try {
      return JSON.parse(sessionStorage.getItem(CHECKOUT_SESSION_STORAGE_TMP_VAT_ID));
    } catch (error) {
      console.error(error);
      clearTmpVatIdFromSessionStorage();
      return null;
    }
  }

  export function clearSubsidiaryNeedsApprovalFromLocalStorage() {
    localStorage.removeItem(SUBSIDIARY_SCOPE_CHECKOUT);
  }

  export function saveSubsidiaryNeedsApprovalOnLocalStorage(): void {
    localStorage.setItem(SUBSIDIARY_SCOPE_CHECKOUT, SUBSIDIARY_NEEDS_APPROVAL);
  }

  export function getSubsidiaryNeedsApprovalFromLocalStorage(): string | null {
    try {
      return JSON.parse(localStorage.getItem(SUBSIDIARY_SCOPE_CHECKOUT));
    } catch (error) {
      console.error(error);
      clearTmpVatIdFromSessionStorage();
      return null;
    }
  }

  export function clearTmpVatIdFromSessionStorage() {
    sessionStorage.removeItem(CHECKOUT_SESSION_STORAGE_TMP_VAT_ID);
  }

  const paypalLanguageMap: Record<LanguageCode | PaypalLocale, PaypalLocale> = {
    de: 'de_DE',
    en: 'en_US',
    es: 'es_ES',
    fr: 'fr_FR',
    nl: 'nl_NL',
    pt: 'pt_PT',
    it: 'it_IT',
    de_DE: 'de_DE',
    en_US: 'en_US',
    es_ES: 'es_ES',
    fr_FR: 'fr_FR',
    nl_NL: 'nl_NL',
    pt_PT: 'pt_PT',
    it_IT: 'it_IT',
  } as const;

  export function determinePaypalButtonLanguage(envLanguage: string): string {
    return paypalLanguageMap[envLanguage.replace('-', '_')] ?? 'de_DE';
  }

  const paypalBuyerCountryMap: Record<LanguageCode | PaypalLocale, PaypalBuyerLocale> = {
    de: 'DE',
    en: 'US',
    es: 'ES',
    fr: 'FR',
    nl: 'NL',
    pt: 'PT',
    it: 'IT',
    de_DE: 'DE',
    en_US: 'US',
    es_ES: 'ES',
    fr_FR: 'FR',
    nl_NL: 'NL',
    pt_PT: 'PT',
    it_IT: 'IT',
  } as const;

  export function determinePaypalBuyerCountryForSandbox(code: string): string {
    return paypalBuyerCountryMap[code.replace('-', '_')] ?? 'DE';
  }

  export const CommonTranslationMarketPostKey: Partial<Record<CountryCode, string>> = {
    [CountryCode.France]: '.FRANCE',
    [CountryCode.Spain]: '.SPAIN',
    [CountryCode.Italy]: '.ITALY',
    [CountryCode.Netherlands]: '.NETHERLANDS',
    [CountryCode.Germany]: '.GERMANY',
    [CountryCode.Portugal]: '.PORTUGAL',
  } as const;

  export function getTranslationMarketPostKey(supportedMarkets: CountryCode[]): string {
    const countryCode = getCountryDomainCode({});
    if (supportedMarkets.includes(countryCode)) {
      return CommonTranslationMarketPostKey[countryCode] ?? '';
    }
    return '';
  }

  export function getFrTranslationPostfix() {
    return getTranslationMarketPostKey([CountryCode.France]);
  }

  export function getAssistedSalesPaymentError(refusalCode: string): PaymentErrorMessages {
    return PaymentFailedAssistedSalesErrorMap[refusalCode] ?? PaymentErrorMessages.ASSISTED_SALES_GENERIC_ERROR;
  }

  export function isRatePayPaymentMethod(method: PaymentMethodOptions | ServicePaymentMethodTypes): boolean {
    return [
      PaymentMethodOptions.RatePayDirectDebit,
      PaymentMethodOptions.RatePayInvoice,
      ServicePaymentMethodTypes.RatePayDirectDebit,
      ServicePaymentMethodTypes.RatePayInvoice,
    ].includes(method);
  }

  export function getPhoneNumberFromReactLocal() {
    return localStorage.getItem(CHECKOUT_LOCALSTORAGE_REACT_PHONENUMBER);
  }

  export function resetPhoneNumberPostPayment(): void {
    localStorage.removeItem(CHECKOUT_LOCALSTORAGE_REACT_PHONENUMBER);
  }

  export namespace CheckoutCustomerConsent {
    export function init(key: SupportedConsentTypes) {
      function toggleFlag(flag: boolean): void {
        try {
          if (!flag) throw new Error('');
          sessionStorage.setItem(key, 'true');
        } catch (e) {
          removeFromSessionStorage();
        }
      }

      function isActive(): boolean {
        try {
          return !!sessionStorage.getItem(key);
        } catch (e) {
          return false;
        }
      }

      function removeFromSessionStorage(): void {
        if (sessionStorage && sessionStorage?.removeItem && typeof sessionStorage?.removeItem === 'function') {
          sessionStorage?.removeItem(key);
        }
      }
      return { toggleFlag, isActive, removeFromSessionStorage };
    }

    export function removeAll(): void {
      if (sessionStorage && sessionStorage?.removeItem && typeof sessionStorage?.removeItem === 'function') {
        sessionStorage?.removeItem(CHECKOUT_SESSION_STORAGE_COMBINED_CUSTOMER_CONSENT_PHONE);
        sessionStorage?.removeItem(CHECKOUT_SESSION_STORAGE_COMBINED_CUSTOMER_CONSENT_EMAIL);
        sessionStorage?.removeItem(CHECKOUT_SESSION_STORAGE_COMBINED_CUSTOMER_CONSENT_COMBINE);
        sessionStorage?.removeItem(CHECKOUT_SESSION_STORAGE_COMBINED_CUSTOMER_OPT_OUT_CHECKBOX);
      }
    }

    export type SupportedConsentTypes =
      | typeof CHECKOUT_SESSION_STORAGE_COMBINED_CUSTOMER_CONSENT_PHONE
      | typeof CHECKOUT_SESSION_STORAGE_COMBINED_CUSTOMER_CONSENT_EMAIL
      | typeof CHECKOUT_SESSION_STORAGE_COMBINED_CUSTOMER_CONSENT_COMBINE
      | typeof CHECKOUT_SESSION_STORAGE_COMBINED_CUSTOMER_OPT_OUT_CHECKBOX;
  }

  export function PageShowDebug(cacheNav: (v: boolean) => any) {
    try {
      if (!window) {
        return;
      }
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) {
          cacheNav(true)
        }
      });
    } catch (e) {
     }
  }

  export function onNavigationDebug(): string {
    try {
      const navType = (performance.getEntriesByType?.('navigation')?.[0] as any)?.type;
      return navType ?? 'unknown'
    } catch(e){
      return 'unknown - not found'
    }
  }
}
